import { Building2, Users, MapPinned, UserCog, MonitorPlay, BookOpen, ClipboardCheck, Home as HomeIcon } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";

const METHOD_IMG = "/images/hero.jpg";

const GROUP = [
  {
    icon: Building2,
    title: "Struttura Organizzativa",
    text: "Gestisce i contatti con le autorità e fornisce gratuitamente tutte le informazioni sui corsi, anche direttamente a domicilio. Per ogni zona un team organizza i corsi secondo una programmazione annuale.",
  },
  {
    icon: Users,
    title: "Struttura Didattica",
    text: "I nostri insegnanti, distribuiti su tutto il territorio, sono professionisti nelle materie e formati internamente a Scuola Sicura sul metodo esclusivo di insegnamento.",
  },
  {
    icon: MapPinned,
    title: "Responsabili di Zona",
    text: "Coordinano l'attività di ogni provincia: dai rapporti diretti con gli studenti a quelli con le autorità, fino alle selezioni dei nuovi docenti.",
  },
  {
    icon: UserCog,
    title: "Formatori",
    text: "Si occupano dell'addestramento dei nuovi docenti e dell'aggiornamento periodico di tutto il corpo insegnanti.",
  },
];

const METHOD = [
  {
    icon: MonitorPlay,
    title: "166 lezioni teorico-pratiche",
    text: "Ogni 10 lezioni pratiche, svolte autonomamente con testi e sussidi multimediali della Scuola, una lezione teorica di due ore in classe con l'insegnante.",
  },
  {
    icon: BookOpen,
    title: "10 argomenti a lezione",
    text: "In ogni lezione si affrontano 10 argomenti, che lo studente assimila e sviluppa a casa nei 10 giorni lavorativi successivi, con manuali e supporti didattici.",
  },
  {
    icon: HomeIcon,
    title: "Aule virtuali",
    text: "L'insegnante verifica con l'allievo lo sviluppo del percorso e la corretta assimilazione delle lezioni, anche a distanza.",
  },
  {
    icon: ClipboardCheck,
    title: "Test di verifica a metà corso",
    text: "Il controllo costante dei progressi prevede un test intermedio che valorizza i risultati conseguiti.",
  },
];

const iconBox =
  "inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-[#1F8296]/30 bg-[#1F8296]/10";

export default function ChiSiamo() {
  return (
    <main>
      <PageHero
        eyebrow="Chi siamo"
        titleLines={["Un team di professionisti", "che guarda ai tuoi obiettivi."]}
        subtitle="Leader nella formazione professionale: l'unica scuola in grado di svolgere l'attività didattica in qualunque città, paese o località, con corsi pratici di informatica dai risultati straordinari. Ed insieme, gli obiettivi li raggiungiamo."
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8" data-testid="about-group">
        <Reveal>
          <p className="font-plex text-xs uppercase tracking-[0.28em] text-[#1F8296]">Il nostro gruppo</p>
          <h2 className="text-balance mt-4 max-w-2xl font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
            Organizzati per garantirti efficienza, senza perdite di tempo
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {GROUP.map((g, i) => (
            <Reveal key={g.title} delay={0.07 * i}>
              <div className="group h-full rounded-3xl border border-[#D8E5E8] bg-white p-8 shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)] transition-colors duration-500 hover:border-[#1F8296]/50">
                <div className={`${iconBox} transition-transform duration-300 group-hover:scale-110`}>
                  <g.icon className="h-5 w-5 text-[#1F8296]" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#1A2E33]">{g.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#5C7178]">{g.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8" data-testid="about-method">
        <div className="grid items-start gap-14 lg:grid-cols-2">
          <Reveal className="lg:sticky lg:top-28">
            <p className="font-plex text-xs uppercase tracking-[0.28em] text-[#1F8296]">Il nostro metodo</p>
            <h2 className="text-balance mt-4 font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
              Pratico, graduale e conciliabile con i tuoi impegni
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5C7178]">
              L'approccio al mondo informatico è essenzialmente pratico e basato sul divertimento e
              sul gioco, in un clima piacevole e stimolante. Le classi sono a numero limitato: ogni
              studente raggiunge al 100% l'obiettivo prefissato, seguito in maniera esclusiva
              dall'insegnante, con la verifica continua della corretta assimilazione della materia.
            </p>
            <div className="mt-8 overflow-hidden rounded-[28px] border border-[#D8E5E8] shadow-[0_20px_60px_-30px_rgba(16,51,59,0.35)]">
              <img
                src={METHOD_IMG}
                alt="Tre allievi anziani davanti a un computer, con l'insegnante al loro fianco"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div className="space-y-5">
            {METHOD.map((m, i) => (
              <Reveal key={m.title} delay={0.07 * i}>
                <div className="flex gap-6 rounded-3xl border border-[#D8E5E8] bg-white p-7 shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)] transition-colors duration-500 hover:border-[#1F8296]/50">
                  <span className="font-plex text-sm font-semibold text-[#1F8296]">0{i + 1}</span>
                  <div>
                    <div className="flex items-center gap-3">
                      <m.icon className="h-5 w-5 text-[#1F8296]" />
                      <h3 className="font-display text-lg font-semibold text-[#1A2E33]">{m.title}</h3>
                    </div>
                    <p className="mt-2.5 text-sm leading-relaxed text-[#5C7178]">{m.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 pb-28 sm:px-8" data-testid="about-teachers">
        <div className="grid gap-5 lg:grid-cols-2">
          <Reveal>
            <div className="h-full overflow-hidden rounded-3xl border border-[#D8E5E8] bg-white shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)]">
              <img
                src="/images/insegnanti.jpg"
                alt="Un insegnante guida due allievi senior al computer"
                className="aspect-[16/8] w-full object-cover"
                loading="lazy"
              />
              <div className="p-9 pt-6">
                <h3 className="font-display text-2xl font-semibold text-[#1A2E33]">Gli insegnanti</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#5C7178] sm:text-base">
                  Laureati o tecnici specialisti, con una specifica formazione didattica. Il percorso di
                  inserimento nel nostro organico valuta non solo le competenze tecniche, ma anche le
                  caratteristiche caratteriali e umane del docente.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full overflow-hidden rounded-3xl border border-[#D8E5E8] bg-white shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)]">
              <img
                src="/images/materiale.jpg"
                alt="Manuali e libri del materiale didattico della Scuola"
                className="aspect-[16/8] w-full object-cover"
                loading="lazy"
              />
              <div className="p-9 pt-6">
                <h3 className="font-display text-2xl font-semibold text-[#1A2E33]">Il materiale didattico</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#5C7178] sm:text-base">
                  Sviluppato dal nostro settore sviluppo e diversificato per ogni percorso: manuali
                  suddivisi in unità formative con immagini ed esercizi, supporti multimediali con video
                  corsi, esercitazioni e unità di progetto. Tutto il materiale resta allo studente alla
                  fine del corso, per consolidare e approfondire le conoscenze.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
